// The published model states the pad is running on.
//
// `lib/models.js` knows how to FETCH one state; this knows which ones today's
// pad actually needs, holds them, and binds them into the `{ [modelId]: {
// predict } }` map `runStrategy` reads out of `ctx.models`. Until this existed,
// every strategy with a model on the pad printed "no model supplied" — the
// engine was right, nobody had supplied one.
//
// THREE KINDS OF MODEL, AND ONLY ONE OF THEM IS FETCHED.
//
//   stateful   elo, dixon-coles, squiggle-ensemble. Ratings or tips fitted on
//              the server and published per sport. These are what gets loaded.
//   stateless  sharp-anchor reads the board in front of it; market-consensus
//              and none never leave the engine. Nothing to fetch.
//   yours      user-upload is bound to whichever probability set is selected,
//              which came off this device and never had a server state.
//
// A MISSING STATE IS NOT BOUND. It would be easy to hand `runStrategy` a
// binder that always answers null, and the strategy would then print "no leg
// cleared the edge test" — which is a lie, because no leg was ever tested. So
// a model with no usable state is left OUT of the map, the engine says "no
// model supplied", and Today turns that into the honest sentence.

import { ref } from 'vue';
import { isDemo } from '../../core/demo.js';
import { prefs } from '../../core/storage.js';
import { bindModel, modelFor } from '../engine/models/index.js';
import { correctsMarket, fitFrom, fitKey, fitProven } from '../engine/encompassing.js';
import { sportLabel, teamIds } from '../engine/teams.js';
import { loadModelState } from '../models.js';

/** Models that cannot predict anything without a state published by the server. */
export const STATEFUL_MODELS = ['elo', 'dixon-coles', 'close-line', 'squiggle-ensemble'];

/** `${modelId}:${sport}` -> the published state. Replaced whole, never mutated. */
export const modelStates = ref({});

/** `${modelId}:${sport}` -> idle | loading | ready | missing | error. */
export const modelStatus = ref({});

/** How a status reads in small print under the editor's segment. */
export const STATUS_WORDS = {
  idle: 'not loaded',
  loading: 'loading',
  ready: 'ready',
  missing: 'no model yet',
  error: 'could not load',
};

/**
 * The key one state is held under, and the key its fit is filed under too —
 * they are the same string, and `fitKey` in the engine is where it is written,
 * because `runStrategy` looks a fit up by it without knowing this store exists.
 */
export const modelKey = fitKey;

/** Is this a model that has to be fetched before it can say anything? */
export const isStateful = (modelId) => STATEFUL_MODELS.includes(modelId);

// --- what a model has been shown to be worth ---------------------------------
//
// A STATE SAYS WHAT THE MODEL THINKS; A FIT SAYS WHETHER ANYBODY SHOULD BET ON
// IT (engine review #12). The fit is the three encompassing coefficients — the
// market's weight, the model's weight, and the intercept — plus the t on the
// model's own weight, which is the number the whole thing turns on. Under 1.96
// there is no correction to make and the strategy prints nothing.
//
// WHERE IT COMES FROM: a backtest. `stores/backtests.js` files one at the end
// of every finished run whose strategy carries a real model, off that run's own
// walk-forward sample, and the pad reads it here. Nothing else writes one, so a
// model nobody has backtested has no fit and says so on the pad — which is the
// honest state of affairs rather than a gap in the app.
//
// AND IT DIES WITH ITS MODEL. `fittedFor` is the state's own `fittedTo` at the
// moment it was measured: a model that has refitted since has not been tested,
// and a fit that outlived the ratings it was measured on is the one way this
// could claim more than anybody checked.

/** Where the fits live: `prefs.modelFits`, `${modelId}:${sport}` -> fit. */
const FITS_KEY = 'modelFits';

/** `${modelId}:${sport}` -> `{ b0, b1, b2, t2, n, fittedAt, fittedFor }`. */
export const modelFits = ref(readFits());

function readFits() {
  try {
    const raw = prefs[FITS_KEY];
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
}

/** What a state calls the moment it was fitted to, or null for a state that does not say. */
const fittedToOf = (state) => state?.fittedTo ?? state?.publishedAt ?? null;

/**
 * File the fit a finished backtest measured. Kept only where it is a fit at
 * all — `fitFrom` answers null for a sample too small to regress — and stamped
 * with the state it was measured against, so a later publish retires it.
 *
 * Returns the fit that was stored, or null.
 */
export function rememberFit(modelId, sport, result, { fittedAt = null } = {}) {
  if (!correctsMarket(modelId) || !sport) return null;
  const fit = fitFrom(result, {
    fittedAt: fittedAt ?? new Date().toISOString(),
    fittedFor: fittedToOf(modelStates.value[modelKey(modelId, sport)]),
  });
  if (!fit) return null;
  const next = { ...modelFits.value, [modelKey(modelId, sport)]: fit };
  modelFits.value = next;
  try { prefs[FITS_KEY] = next; } catch { /* private mode */ }
  return fit;
}

/**
 * Drop every fit measured against a state that has since refitted. Called when
 * states land, because that is the only moment the answer can change.
 */
function retireStaleFits(states) {
  const kept = {};
  let dropped = 0;
  for (const [key, fit] of Object.entries(modelFits.value)) {
    const state = states[key];
    // A fit for a model this device has not loaded is left alone: it is not
    // stale, it is simply not in use today.
    if (state && fit?.fittedFor !== undefined && fit.fittedFor !== fittedToOf(state)) {
      dropped += 1;
      continue;
    }
    kept[key] = fit;
  }
  if (!dropped) return;
  modelFits.value = kept;
  try { prefs[FITS_KEY] = kept; } catch { /* private mode */ }
}

/** The fits as `runStrategy` reads them: proven ones only, everything else absent. */
export function provenFits(fits = modelFits.value) {
  const out = {};
  for (const [key, fit] of Object.entries(fits ?? {})) {
    if (fitProven(fit)) out[key] = fit;
  }
  return out;
}

/** The sports a model covers, narrowed to the ones asked about. `null` means all of them. */
function sportsFor(modelId, sports) {
  const covered = modelFor(modelId)?.sports ?? null;
  const asked = sports ?? [];
  return covered === null ? [...asked] : asked.filter((s) => covered.includes(s));
}

/**
 * Every (model, sport) the given strategies need a published state for.
 *
 * A strategy with an empty sports filter runs on everything, so it needs the
 * model in every sport on the board today. A sport the model does not cover
 * (Elo has no Premier League) is dropped rather than requested: a 404 the code
 * already knew about is noise in the log and "no model yet" on a screen where
 * the honest answer is "not that sport".
 */
export function modelsNeededFor(strategies, sports) {
  const seen = new Set();
  const out = [];
  for (const strategy of strategies ?? []) {
    const modelId = strategy?.probabilitySource?.model;
    if (!isStateful(modelId)) continue;
    const filter = strategy?.filters?.sports ?? [];
    for (const sport of sportsFor(modelId, filter.length ? filter : (sports ?? []))) {
      const key = modelKey(modelId, sport);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ modelId, sport });
    }
  }
  return out;
}

/**
 * WHICH SPORTS THIS STRATEGY'S MODEL IS PUBLISHED FOR — what the pad names in
 * the sentence it prints when nothing was dealt (`lib/stores/pad-notes.js`).
 *
 * The model's own coverage (`elo.sports`, the same list `publish-models.mjs`
 * writes states for) narrowed by the strategy's sport filter, because a
 * strategy that only ever bets the AFL does not "rate" the BBL whatever its
 * model can do. A model that covers everything (`sports: null`) has no list of
 * its own, so it answers with the filter's, and an unfiltered one answers
 * nothing — there is no finite set of sports to name.
 *
 * This is `sportsFor` asked the other way round: that one narrows the BOARD to
 * what a model covers, this narrows the MODEL to what a strategy will bet.
 */
export function coveredSportsFor(strategy) {
  const covered = modelFor(strategy?.probabilitySource?.model)?.sports ?? null;
  const filter = strategy?.filters?.sports ?? [];
  if (covered === null) return [...filter];
  const narrowed = filter.length ? covered.filter((s) => filter.includes(s)) : [...covered];
  // A FORK WHOSE FILTER RULES OUT EVERYTHING ITS MODEL COVERS — Elo Edge tuned
  // to the Premier League — narrows to nothing, and a sentence with nothing in
  // it is the shrug this replaced. The model's own list is then the fact the
  // reader needs, and it is the one that explains why the section is empty.
  return narrowed.length ? narrowed : [...covered];
}

/**
 * A flat, plausible state so /demo has something to print.
 *
 * Every team on the same rating is not a model — it prices every match at the
 * home-ground advantage and nothing else. That is the point: the demo shows
 * what a model-driven ticket LOOKS like without pretending to know who wins.
 * Squiggle stays MISSING in /demo and says so: its opinion is a feature on a
 * real fixture (the day's tips), not a state, so a flat state would bind a
 * model that then answered null on every leg — which prints "no leg cleared the
 * edge test" about legs nothing ever tested.
 */
function demoState(modelId, sport) {
  // THE CLOSE-LINE DEMO SAYS THE PRICE IS GOING NOWHERE, which is the flat-Elo
  // trick again: a single weight of one on the opening probability and zero on
  // everything else, so the "expected close" IS the price on the board. It
  // prints what a close-chasing ticket looks like without inventing a drift
  // nobody measured. No team ids are needed for it — the features are prices.
  if (modelId === 'close-line') {
    return {
      weights: [0, 1, 0, 0, 0, 0],
      means: [0, 0, 0, 0, 0],
      sds: [1, 1, 1, 1, 1],
      sports: [],
      n: 0,
      params: {},
      fittedTo: new Date(0).toISOString(),
      demo: true,
    };
  }
  const ids = teamIds(sport);
  if (!ids.length) return null;
  if (modelId === 'elo') {
    return {
      ratings: Object.fromEntries(ids.map((id) => [id, 1500])),
      hga: { [sport]: 35 },
      params: {},
      fittedTo: new Date(0).toISOString(),
      demo: true,
    };
  }
  if (modelId === 'dixon-coles') {
    return {
      attack: Object.fromEntries(ids.map((id) => [id, 0])),
      defence: Object.fromEntries(ids.map((id) => [id, 0])),
      homeAdv: 0.25,
      rho: -0.1,
      params: {},
      fittedTo: new Date(0).toISOString(),
      demo: true,
    };
  }
  return null;
}

/**
 * The fit /demo runs on, beside the flat ratings it runs on.
 *
 * A model with no fit prints nothing and says why, which is the honest thing
 * and also a demo of one sentence. The sample has to show the other half — a
 * model correcting a market price by a small fitted weight — so it carries a
 * fit as plainly made up as the ratings it sits beside: the market nearly whole
 * (b1), a fifteen-point weight on the model (b2), and a t that clears. It is
 * never persisted and never leaves /demo.
 */
const DEMO_FIT = {
  b0: 0, b1: 0.94, b2: 0.15, t2: 3.1, n: 220, fittedAt: new Date(0).toISOString(), demo: true,
};

function setStatus(key, status) {
  modelStatus.value = { ...modelStatus.value, [key]: status };
}

/**
 * Make sure every state in `needs` has been asked for. Never throws and never
 * rejects: a state that cannot be fetched is a status, not an exception.
 *
 * Cheap to call often. A key that has already resolved — ready, missing or
 * error — is not asked for again, because `loadModelState` caches the answer
 * for the session anyway.
 */
export async function ensureModels(needs) {
  const wanted = (needs ?? []).filter(({ modelId, sport }) => !modelStatus.value[modelKey(modelId, sport)]);
  if (!wanted.length) return modelStates.value;

  for (const { modelId, sport } of wanted) setStatus(modelKey(modelId, sport), 'loading');

  const loaded = await Promise.all(wanted.map(async ({ modelId, sport }) => {
    if (isDemo.value) return { modelId, sport, state: demoState(modelId, sport) };
    try {
      return { modelId, sport, state: await loadModelState(modelId, sport) };
    } catch {
      return { modelId, sport, state: null, failed: true };
    }
  }));

  const states = { ...modelStates.value };
  const status = { ...modelStatus.value };
  const demoFits = {};
  for (const { modelId, sport, state, failed } of loaded) {
    const key = modelKey(modelId, sport);
    if (state) {
      states[key] = state;
      status[key] = 'ready';
      if (isDemo.value && correctsMarket(modelId)) {
        demoFits[key] = { ...DEMO_FIT, fittedFor: fittedToOf(state) };
      }
    } else {
      status[key] = failed ? 'error' : 'missing';
    }
  }
  if (Object.keys(demoFits).length) modelFits.value = { ...modelFits.value, ...demoFits };
  modelStates.value = states;
  modelStatus.value = status;
  // A state that has refitted since a fit was measured takes the fit with it.
  retireStaleFits(states);
  return modelStates.value;
}

/** The state for a probability set, in the shape `userUpload.predict` reads. */
const probSetState = (probSet) => probSet?.state ?? { rows: probSet?.rows ?? {} };

/**
 * `ctx.models` for one pad: every model the strategies name that can actually
 * answer, and nothing else.
 *
 * A stateful model's `predict` routes on `match.sport`, because one pad can
 * hold an AFL strategy and an EPL one and they do not share ratings. The
 * per-sport states are snapshotted here rather than read from the ref inside
 * `predict`: `predict` runs once per candidate per leg, and a reactive read in
 * that loop would make every ticket depend on the whole map.
 */
export function bindPadModels(strategies, sports, probSet) {
  const states = modelStates.value;
  const out = {};

  for (const strategy of strategies ?? []) {
    const modelId = strategy?.probabilitySource?.model;
    if (!modelId || modelId === 'none' || modelId === 'market-consensus' || out[modelId]) continue;
    const model = modelFor(modelId);
    if (!model) continue;

    if (modelId === 'user-upload') {
      out[modelId] = bindModel(model, probSetState(probSet));
      continue;
    }
    if (!isStateful(modelId)) {
      // sharp-anchor and anything else that reads the board it is handed.
      out[modelId] = bindModel(model, {});
      continue;
    }

    const bySport = {};
    for (const sport of sportsFor(modelId, sports ?? [])) {
      const state = states[modelKey(modelId, sport)];
      if (state) bySport[sport] = state;
    }
    // Nothing published for any sport on the board: leave it out, so the engine
    // says "no model supplied" instead of quietly testing no legs.
    if (!Object.keys(bySport).length) continue;

    out[modelId] = {
      id: modelId,
      predict(match, selection) {
        const state = bySport[match?.sport];
        return state ? model.predict(state, match, selection) : null;
      },
    };
  }

  return out;
}

/** "AFL ready · NRL no model yet" for one model, or '' when it needs no state. */
export function modelStatusLine(modelId, sports) {
  const needs = modelsNeededFor([{ probabilitySource: { model: modelId }, filters: { sports } }], sports);
  return needs
    .map(({ sport }) => `${sportLabel(sport)} ${STATUS_WORDS[modelStatus.value[modelKey(modelId, sport)] ?? 'idle']}`)
    .join(' · ');
}

/** Every status one strategy's model is in right now — what Today's stub reads. */
export function statusesFor(strategy, sports) {
  return modelsNeededFor([strategy], sports)
    .map(({ modelId, sport }) => modelStatus.value[modelKey(modelId, sport)] ?? 'idle');
}
